import { FormattedMessage, useIntl } from "react-intl";
import styles from "./TermInfoRollupDetailsDialog.module.scss";
import { Dialog } from "../../../../common/core-ui/Dialog.tsx";
import { useProgramDetails } from "./coursesLoader.ts";
import RequiredCodeTag from "../common/RequiredCodeTag.tsx";
import { ReactNode } from "react";
import PillTag from "../common/PillTag.tsx";
import useTermOptDeadline from "./useTermOptDeadline.tsx";

export type DialogType =
  | "Purchase"
  | "IaOptedIn"
  | "IaOptedOut"
  | "IaMixed"
  | "EaOptedIn"
  | "EaOptedOut";

export default function TermInfoRollupDetailsDialog({
  type,
  onRequestClose,
  title,
}: {
  type: DialogType;
  onRequestClose: () => void;
  title: string;
}) {
  const intl = useIntl();
  const {
    ea: { name: eaProgramName },
    name: programName,
  } = useProgramDetails();
  const { optDeadline } = useTermOptDeadline();

  const formatter = {
    p: (text: ReactNode) => <div className={styles.textBlock}>{text}</div>,
    strong: (text: ReactNode) => <strong>{text}</strong>,
    required: () => (
      <RequiredCodeTag code="required" className={styles.codeTag} />
    ),
    optional: () => (
      <RequiredCodeTag code="optional" className={styles.codeTag} />
    ),
    optedInTag: () => <PillTag type="OptedIn" />,
    optedOutTag: () => <PillTag type="OptedOut" />,
  };

  const sharedValues = {
    ...formatter,
    programName,
    optOutDate: optDeadline && new Date(optDeadline),
  };

  const getDialogContent = (type: DialogType) => {
    switch (type) {
      case "Purchase":
        return (
          <FormattedMessage
            defaultMessage="<p>These items were adopted for your course, but need to be purchased and paid for individually. Multiple format types (ie. Print, eBook) may be available to choose from.</p>"
            values={formatter}
          />
        );
      case "EaOptedIn":
        return (
          <FormattedMessage
            defaultMessage="<p>Items marked <optedInTag></optedInTag> are included in your institution''s <strong>{programName}</strong> and will be charged to your student account. You can start accessing them right away, or you can opt out until {optOutDate, date, short}</p>"
            values={{ ...sharedValues, programName: eaProgramName }}
          />
        );
      case "EaOptedOut":
        return (
          <FormattedMessage
            defaultMessage="<p>Items marked <optedOutTag></optedOutTag> mean you have chosen to not get this item through your institution''s <strong>{programName}</strong>. You can purchase these items individually at retail cost, or opt back in until {optOutDate, date, short}.</p>"
            values={{ ...sharedValues, programName: eaProgramName }}
          />
        );
      case "IaOptedIn":
        return (
          <FormattedMessage
            defaultMessage="<p>Items marked <optedInTag></optedInTag> are included in your institution''s <strong>{programName}</strong> and will be charged to your student account. You can start accessing them right away, or you can opt out until {optOutDate, date, short}</p>"
            values={sharedValues}
          />
        );
      case "IaOptedOut":
        return (
          <FormattedMessage
            defaultMessage="<p>Items marked <optedOutTag></optedOutTag> mean you have chosen to not get this item through your institution''s <strong>{programName}</strong>, or the items are physical and are not included in the program. You can purchase these items individually at retail cost or opt back in until {optOutDate, date, short}.</p>"
            values={sharedValues}
          />
        );
      case "IaMixed":
        return (
          <FormattedMessage
            defaultMessage="<p>Items marked <optedInTag></optedInTag> are included in your institution''s <strong>{programName}</strong> and will be charged to your student account. You can start accessing them right away, or you can opt out until {optOutDate, date, short}.</p><p>Items marked <optedOutTag></optedOutTag> mean you have chosen to not get this item through your institution''s <strong>{programName}</strong>, or the items are physical and are not included in the program. You can purchase these items individually at retail cost or opt back in until {optOutDate, date, short}.</p>"
            values={sharedValues}
          />
        );
      default: {
        const exhaustive: never = type;
        return exhaustive;
      }
    }
  };

  const content = getDialogContent(type);

  return (
    <Dialog
      onRequestClose={onRequestClose}
      title={title}
      dialogClassName={styles.container}
      primaryButton={{
        label: intl.formatMessage({ defaultMessage: "Got it" }),
        onClick: onRequestClose,
      }}
    >
      {content}
      <FormattedMessage
        defaultMessage={
          "<p>Items with a <required></required> tag are required by your instructor and should be purchased before the course begins. Items with an <optional></optional> tag are recommended but not required.</p>"
        }
        values={formatter}
      />
    </Dialog>
  );
}
